import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type { UpdateTarget } from "@cd/core";
import { api, ApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Skeleton } from "../../components/ui/primitives";

const list = (names: string[]) => names.map((n) => `“${n}”`).join(", ");

/**
 * Update a copy to the listing's latest version, in place: it keeps its link, shares, title and
 * filters. Changes the user made to the listing's widgets are replaced, so the dialog lists them first.
 */
export function UpdateDialog({ open, onOpenChange, listingId, target, title }: {
  open: boolean; onOpenChange: (o: boolean) => void; listingId: string; target: UpdateTarget; title: string;
}) {
  const qc = useQueryClient();
  const key = ["listing-update", listingId, target];
  const preview = useQuery({ queryKey: key, queryFn: () => api.updatePreview(listingId, target), enabled: open, staleTime: 0 });
  const update = useMutation({
    mutationFn: () => api.updateCopy(listingId, { ...target, revision: preview.data!.revision }),
    onSuccess: ({ dashboardId }) => {
      if (dashboardId) void qc.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      void qc.invalidateQueries({ queryKey: ["dashboards"] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
      void qc.invalidateQueries({ queryKey: ["marketplace"] });
      onOpenChange(false);
    },
    // The copy changed after the preview: show the fresh one rather than overwrite unseen edits.
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: key });
    },
  });

  useEffect(() => {
    if (open) update.reset();
  }, [open]);

  const p = preview.data;
  const r = p?.replaces;
  const changes = r && [
    r.edited.length > 0 && `your edits to ${list(r.edited)}`,
    r.removed.length > 0 && `${list(r.removed)}, which you removed (it comes back)`,
    r.layout && "the way you arranged its widgets",
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Update ${title}`} className="w-[480px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Update “{title}”{p && ` to v${p.to}`}?</h2>
      {preview.isLoading && <Skeleton className="mt-4 h-24" />}
      {preview.error && <p className="mt-4 text-sm text-bad">{preview.error.message}</p>}
      {p && (
        <div className="mt-2 space-y-3 text-sm text-muted">
          <p>You have v{p.from}. Your copy is updated where it is, and keeps its link, name, filters and who it's shared with.</p>
          {p.versions.some((v) => v.notes) && (
            <ul className="space-y-1 rounded-lg bg-surface-2 px-3 py-2 text-xs">
              {p.versions.map((v) => (
                <li key={v.version}><span className="font-medium text-foreground">v{v.version}</span>{v.notes && ` · ${v.notes}`}</li>
              ))}
            </ul>
          )}
          {(r === null || (changes && changes.length > 0)) && (
            <div className="flex gap-2 rounded-lg bg-warn-bg px-3 py-2.5 text-warn-fg">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <p>
                {r === null
                  ? "Any changes you made to it will be replaced."
                  : <>This replaces {changes!.join("; ")}.</>}
              </p>
            </div>
          )}
          {p.kept.length > 0 && <p>Widgets you added yourself ({list(p.kept)}) stay, below the updated ones.</p>}
        </div>
      )}
      {update.error && (
        <p className="mt-4 text-sm text-bad">
          {update.error instanceof ApiError && update.error.status === 409 ? "Your copy changed in the meantime. Check what's replaced above, then update again." : update.error.message}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="primary" disabled={!p || preview.isFetching || update.isPending} onClick={() => update.mutate()}>
          {r === null || (changes && changes.length > 0) ? "Replace my changes and update" : "Update"}
        </Button>
      </div>
    </Dialog>
  );
}
